import { MIRA_INFINITA, MIRA_PADRAO_M, MIRA_TETO_M } from '@sol-pool/protocol'
import { idiomaAtual, type Idioma } from '@/i18n'

/**
 * O alcance da mira de uma mesa, na tela.
 *
 * O modo é UM NÚMERO — metros de linha-guia, com `-1` = infinito — e os três
 * modos que se pede no lobby são presets dele. Este módulo traduz número em
 * texto, e é puro de propósito: devolve CHAVE e parâmetros, nunca frase
 * montada. Concatenar "alcance de " com o número deixaria a mesa customizada
 * em português no cliente em inglês.
 */

/** Os três presets, na ordem em que aparecem no seletor. */
export const MIRA_PRESETS = [
  { valor: 0, chave: 'mira.sem' },
  { valor: MIRA_PADRAO_M, chave: 'mira.padrao' },
  { valor: MIRA_INFINITA, chave: 'mira.estendida' },
] as const

export const miraEhPreset = (valor: number): boolean =>
  MIRA_PRESETS.some((p) => p.valor === valor)

/** Chave de tradução e, quando há, os valores a interpolar. */
export type TextoDaMira = { chave: string; params?: Record<string, string> }

/**
 * O alcance, preso no que o protocolo aceita.
 *
 * EXPORTADO porque o menu do treino também precisa dele: lá a escolha vem de
 * uma preferência guardada no navegador, e uma preferência estragada não pode
 * apagar o menu nem premiar com trajetória completa quem não escolheu.
 *
 * Lixo cai no PADRÃO, como em `tracosDaMira` e pelo mesmo motivo: premiar um
 * `NaN` com a descrição de "trajetória completa" numa mesa apostada seria
 * exploit, e mostrar "infinito" numa mesa que não é seria mentira na tela.
 */
export const miraEscolhida = (valor: number): number => {
  if (!Number.isFinite(valor)) return MIRA_PADRAO_M
  if (valor === MIRA_INFINITA) return MIRA_INFINITA
  if (valor < 0 || valor > MIRA_TETO_M) return MIRA_PADRAO_M
  return valor
}

/**
 * O número como o idioma escreve.
 *
 * Vírgula em português, ponto em inglês: o separador decimal é do idioma, e
 * "1.5 m" num cliente pt-BR é texto estrangeiro no meio da frase.
 */
const emMetros = (valor: number, idioma: Idioma): string =>
  new Intl.NumberFormat(idioma, { maximumFractionDigits: 2 }).format(valor)

/** A frase que explica o que a mesa mostra. */
export function textoDaMira(valor: number, idioma: Idioma = idiomaAtual()): TextoDaMira {
  const m = miraEscolhida(valor)
  if (m === 0) return { chave: 'mira.sem.explica' }
  if (m === MIRA_INFINITA) return { chave: 'mira.estendida.explica' }
  if (m === MIRA_PADRAO_M) return { chave: 'mira.padrao.explica' }
  return { chave: 'mira.custom.explica', params: { m: emMetros(m, idioma) } }
}

/**
 * O rótulo curto, para a listagem do lobby.
 *
 * Quem olha o lobby precisa ver o modo ANTES de depositar: o modo é imutável
 * depois, e entrar sem saber é escolher às cegas.
 */
export function rotuloDaMira(valor: number, idioma: Idioma = idiomaAtual()): TextoDaMira {
  const m = miraEscolhida(valor)
  const preset = MIRA_PRESETS.find((p) => p.valor === m)
  if (preset) return { chave: preset.chave }
  return { chave: 'mira.custom.curto', params: { m: emMetros(m, idioma) } }
}

/** O mínimo que se precisa saber de um evento ou de uma sala para achar a mira. */
type TemMira = { miraM: number } | null | undefined

/**
 * De ONDE vem o alcance da partida que está na tela.
 *
 * O EVENTO manda quando a partida é de um; a SALA manda no resto. A tela lia
 * só a sala, e partida de evento não tem sala: melhor de 3 e torneio jogavam
 * SEMPRE na mira normal, sem ninguém ter escolhido — e era provavelmente por
 * isso que o teste da estendida (#44) "ficava normal".
 *
 * Sem nenhum dos dois, a mira de hoje: é o caso do treino e o da cena que
 * nasce antes de a sala ser conhecida (a mesa abre quando a partida começa).
 *
 * Passa por `miraEscolhida` como tudo aqui — um `NaN` premiado com trajetória
 * completa numa mesa apostada seria exploit, não descuido.
 */
export function miraDaPartida(evento: TemMira, sala: TemMira): number {
  const bruto = evento ? evento.miraM : sala ? sala.miraM : MIRA_PADRAO_M
  return miraEscolhida(bruto)
}

// ------------------------------------------- a preferência do treino

/** O mínimo de `Storage` que se usa: dá para passar um objeto qualquer no teste. */
type Armazem = Pick<Storage, 'getItem' | 'setItem'>

const CHAVE_DA_MIRA_DO_TREINO = 'treino.mira'

/**
 * A mira com que o treino abre.
 *
 * SÓ do treino: na mesa apostada quem manda é a sala ou o evento, porque a
 * escolha dá ou tira vantagem sobre o adversário — aqui não há adversário, e
 * por isso ela nem chega ao servidor.
 *
 * Qualquer coisa que não seja um alcance válido vira a mira de hoje, pelo
 * mesmo motivo de `miraEscolhida`: alguém editou o `localStorage` à mão, ou
 * uma versão antiga gravou outra coisa ali, e o treino não pode abrir com
 * `NaN` de alcance.
 */
export function lerMiraDoTreino(armazem: Armazem | null): number {
  try {
    const bruto = armazem?.getItem(CHAVE_DA_MIRA_DO_TREINO)
    if (bruto === null || bruto === undefined || bruto === '') return MIRA_PADRAO_M
    return miraEscolhida(Number(bruto))
  } catch {
    return MIRA_PADRAO_M
  }
}

/** Guarda a escolha. Falhar em silêncio é o certo: é preferência de treino. */
export function guardarMiraDoTreino(armazem: Armazem | null, valor: number): void {
  try {
    armazem?.setItem(CHAVE_DA_MIRA_DO_TREINO, String(miraEscolhida(valor)))
  } catch {
    // Janela privada ou dados bloqueados: o treino segue com o que está na mesa.
  }
}
